import { defineStore } from 'pinia'
import { ref } from 'vue'
import api from '../api'
import { useUserStore } from './userStore'
import type { ResourceType, ActiveUserDto, PresenceStatusDto } from '../dtos'

export const usePresenceStore = defineStore('presence', () => {
    const userStore = useUserStore()
    const activeUsers = ref<ActiveUserDto[]>([])
    const isLocked = ref(false)
    const currentResource = ref<{ type: ResourceType, id: number } | null>(null)
    
    let heartbeatInterval: any = null
    let statusInterval: any = null

    async function sendHeartbeat() {
        if (!currentResource.value || !userStore.user) return
        try {
            const token = await userStore.user.getIdToken()
            await api.post('/presence/heartbeat', {
                resourceType: currentResource.value.type,
                resourceId: currentResource.value.id
            }, {
                headers: { Authorization: `Bearer ${token}` }
            })
        } catch (error) {
            console.error('Heartbeat failed', error)
        }
    }

    async function fetchStatus() {
        if (!currentResource.value || !userStore.user) return
        try {
            const token = await userStore.user.getIdToken()
            const response = await api.get<PresenceStatusDto>(
                `/presence/status/${currentResource.value.type}/${currentResource.value.id}`,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            )
            activeUsers.value = response.data.activeUsers
            isLocked.value = response.data.isLocked
        } catch (error) {
            console.error('Failed to fetch presence status', error)
        }
    }

    function startPresence(type: ResourceType, id: number) {
        stopPresence()
        currentResource.value = { type, id }
        
        // Initial calls
        sendHeartbeat()
        fetchStatus()

        // Set up intervals (Heartbeat every 30s, Status every 10s)
        heartbeatInterval = setInterval(sendHeartbeat, 30000)
        statusInterval = setInterval(fetchStatus, 10000)
    }

    function stopPresence() {
        if (heartbeatInterval) clearInterval(heartbeatInterval)
        if (statusInterval) clearInterval(statusInterval)
        heartbeatInterval = null
        statusInterval = null
        currentResource.value = null
        activeUsers.value = []
        isLocked.value = false
    }

    /**
     * Performs an immediate, on-demand check for other users.
     * Returns true if the resource is currently locked by others.
     */
    async function checkConcurrency(type: ResourceType, id: number): Promise<boolean> {
        if (!userStore.user) return false
        try {
            const token = await userStore.user.getIdToken()
            const response = await api.get<PresenceStatusDto>(
                `/presence/status/${type}/${id}`,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            )
            activeUsers.value = response.data.activeUsers
            isLocked.value = response.data.isLocked
            return response.data.isLocked
        } catch (error) {
            console.error('Failed to check concurrency on-demand', error)
            return false
        }
    }

    return {
        activeUsers,
        isLocked,
        currentResource,
        startPresence,
        stopPresence,
        checkConcurrency
    }
})
